import { Filter, SimplePlanWithFilters } from './filter.type'
import { AssetDataGeoJsonLayer } from './asset.type'
import {
  setExcludedPropertyToAssetsBySubFilter,
  filterAssetsByInventory,
  addIncludedAssetsFromExcludeLayer,
  filterAssetsByInventoryOverwrittingAssets,
} from './asset.helper'
import {
  filterAssetsByMediaType,
  filterAssetsByDigital,
} from './mediatype.helper'
import { filterAssetsByPackages } from './packages.helper'
import { filterAssetsByCountry } from './country.helper'
import { FilterAppliedResult } from './plan.type'
import { getAppMetadata } from '../metadata/metadata'
import { Environment } from '@workspaces/types'

import { isPackagagesFeatureEnabled } from '../metadata/metadata.helper'

function getAssetsUnionCombination(
  assets1: AssetDataGeoJsonLayer[],
  assets2: AssetDataGeoJsonLayer[],
): AssetDataGeoJsonLayer[] {
  if (assets1.length === 0) {
    return assets2
  }
  if (assets2.length === 0) {
    return assets1
  }
  const allAssets = [...assets1, ...assets2]
  const assetsAfterUnion = [
    ...new Map(allAssets.map((item) => [item.properties.id, item])).values(),
  ]

  return assetsAfterUnion
}

/**
 * Get the resultant assets from applying a sub filter to all the assets
 * @param assets Assets to be analyzed
 * @param subFilter Porperties of the sub filter to be applied
 * @returns Assets that match the sub filter and intersect with the assets passed as parameter
 */
async function filterAssetsFromSubFilter(
  _environment: Environment.EnvironmentResolver,
  assets: AssetDataGeoJsonLayer[],
  subFilter: Filter,
): Promise<FilterAppliedResult> {
  const metadata = getAppMetadata()
  let assetsFiltered: AssetDataGeoJsonLayer[] = [...assets]
  let appliedFilterAtAnyStage = false

  // Country filter
  if (metadata.plan_filters.widget_countries.enable) {
    const t0 = new Date().getTime()
    const countriesPartialFilterResult: FilterAppliedResult =
      filterAssetsByCountry(assetsFiltered, subFilter.countries)
    console.debug(
      `           🔍 Countries || IN: ${assetsFiltered.length}  OUT: ${
        countriesPartialFilterResult.assets.length
      }  TIME: ${new Date().getTime() - t0}`,
    )
    assetsFiltered = countriesPartialFilterResult.assets
    appliedFilterAtAnyStage =
      countriesPartialFilterResult.filterApplied || appliedFilterAtAnyStage
  }

  // Digital filter
  if (
    metadata.plan_filters.widget_media_type.enable &&
    metadata.plan_filters.widget_media_type.filter_digital
  ) {
    const t0 = new Date().getTime()
    const digitalPartialResult: FilterAppliedResult = filterAssetsByDigital(
      assetsFiltered,
      subFilter,
    )
    console.debug(
      `           🔍 Digital || IN: ${assetsFiltered.length}  OUT: ${
        digitalPartialResult.assets.length
      }  TIME: ${new Date().getTime() - t0}`,
    )

    assetsFiltered = digitalPartialResult.assets
    appliedFilterAtAnyStage =
      digitalPartialResult.filterApplied || appliedFilterAtAnyStage
  }

  // Media types filter
  if (metadata.plan_filters.widget_media_type.enable) {
    const t0 = new Date().getTime()
    const mediaTypePartialResult: FilterAppliedResult = filterAssetsByMediaType(
      assetsFiltered,
      subFilter,
    )
    console.debug(
      `           🔍 Media Type || IN: ${assetsFiltered.length}  OUT: ${
        mediaTypePartialResult.assets.length
      }  TIME: ${new Date().getTime() - t0}`,
    )

    assetsFiltered = mediaTypePartialResult.assets
    appliedFilterAtAnyStage =
      mediaTypePartialResult.filterApplied || appliedFilterAtAnyStage
  }

  // Packages filter
  if (metadata.plan_filters.widget_packages.enable) {
    const t0 = new Date().getTime()
    const packagesPartialResult: FilterAppliedResult = filterAssetsByPackages(
      assetsFiltered,
      subFilter,
    )
    console.debug(
      `           🔍 Packages || IN: ${assetsFiltered.length}  OUT: ${
        packagesPartialResult.assets.length
      }  TIME: ${new Date().getTime() - t0}`,
    )

    assetsFiltered = packagesPartialResult.assets
    appliedFilterAtAnyStage =
      packagesPartialResult.filterApplied || appliedFilterAtAnyStage
  }

  // Inventory assets ids filter
  if (metadata.plan_filters.widget_assets.enable) {
    const t0 = new Date().getTime()
    let inventoryPartialResult: FilterAppliedResult
    if (!subFilter?.panels.ids) {
      inventoryPartialResult = { assets: assetsFiltered, filterApplied: false }
    } else {
      if (isPackagagesFeatureEnabled(metadata)) {
        // If package feature is enable, means FI flavour, we need to set only the assets that are provided in the uploaded file
        inventoryPartialResult = filterAssetsByInventoryOverwrittingAssets(
          assets,
          subFilter,
        )
      } else {
        inventoryPartialResult = filterAssetsByInventory(
          assets,
          assetsFiltered,
          subFilter,
        )
      }
    }
    console.debug(
      `           🔍 Inventory || IN: ${assetsFiltered.length}  OUT: ${
        inventoryPartialResult.assets.length
      }  TIME: ${new Date().getTime() - t0}`,
    )

    assetsFiltered = inventoryPartialResult.assets
    appliedFilterAtAnyStage =
      inventoryPartialResult.filterApplied || appliedFilterAtAnyStage
  }

  // Included assets via map
  let t0 = new Date().getTime()
  const includedAssetsPartialResult: FilterAppliedResult =
    addIncludedAssetsFromExcludeLayer(
      assets,
      assetsFiltered,
      subFilter.assets_included,
    )
  console.debug(
    `           🔍 Included || IN: ${assetsFiltered.length}  OUT: ${
      includedAssetsPartialResult.assets.length
    }  TIME: ${new Date().getTime() - t0}`,
  )
  assetsFiltered = includedAssetsPartialResult.assets
  appliedFilterAtAnyStage =
    includedAssetsPartialResult.filterApplied || appliedFilterAtAnyStage

  // Exclude assets
  t0 = new Date().getTime()
  const assetsFilteredExcluded = setExcludedPropertyToAssetsBySubFilter(
    assets,
    assetsFiltered,
    [...subFilter.assets_excluded],
  )
  console.debug(
    `           🔍 Excluded || IN: ${assetsFiltered.length}  OUT: ${
      assetsFilteredExcluded.length
    }  TIME: ${new Date().getTime() - t0}`,
  )
  assetsFiltered = assetsFilteredExcluded
  appliedFilterAtAnyStage =
    appliedFilterAtAnyStage || subFilter.assets_excluded.length > 0

  return {
    assets: assetsFiltered,
    filterApplied: appliedFilterAtAnyStage,
  }
}

/**
 *
 * @param assets Assets to be analyzed
 * @param planWithFilters Sub filters that compose the filter defined by the user
 * @returns Assets that match the filter defined by the user and intersect with the assets passed as parameter
 */
export async function filterAssetsFromPlan(
  environment: Environment.EnvironmentResolver,
  assets: AssetDataGeoJsonLayer[],
  planWithFilters: SimplePlanWithFilters,
): Promise<AssetDataGeoJsonLayer[]> {
  let assetsFiltered: AssetDataGeoJsonLayer[] = []
  let filterAppliedAtAnySubFilter = false

  let num = 0

  for (const subFilter of planWithFilters.filters) {
    console.debug(`🟪 Plan: 🔍 Subfilter: ${num}`)
    const {
      assets: assetsFilteredFromSubPlan,
      filterApplied,
    }: FilterAppliedResult = await filterAssetsFromSubFilter(
      environment,
      assets,
      subFilter,
    )
    filterAppliedAtAnySubFilter = filterAppliedAtAnySubFilter || filterApplied
    if (filterApplied) {
      assetsFiltered = getAssetsUnionCombination(
        assetsFiltered,
        assetsFilteredFromSubPlan,
      )
    }
    num++
  }

  return filterAppliedAtAnySubFilter ? assetsFiltered : assets
}

/**
 *
 * @param assets Assets to be analyzed
 * @param excluded List of assets ids to be excluded
 * @returns Assets with the excluded property set to true if the asset id is in the excluded list
 */
export async function excludeAssetsFromPlan(
  assets: AssetDataGeoJsonLayer[],
  excluded: string[],
): Promise<AssetDataGeoJsonLayer[]> {
  return assets.map((asset) => {
    asset.properties.excluded = false
    if (excluded.includes(asset.properties.id)) {
      asset.properties.excluded = true
    }
    return asset
  })
}
